import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";
import { FaEdit, FaTimes, FaCheck, FaSpinner } from "react-icons/fa";
const AdminContactsSkeleton = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
      {/* HEADER SKELETON */}
      <div className="bg-white border p-6 rounded">
        <div className="flex justify-between items-center mb-6">
          <div className="h-6 w-40 bg-gray-200 rounded"></div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>

        {/* FORM GRID */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-4">
            {/* MAP LINK */}
            <div>
              <div className="h-4 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-24 w-full bg-gray-200 rounded"></div>
            </div>

            {/* SOCIALS SKELETON */}
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="border p-3 rounded-lg bg-gray-100">
                <div className="flex justify-between mb-2">
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  <div className="h-6 w-20 bg-gray-200 rounded"></div>
                </div>
                <div className="h-10 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const InputField = ({ label, value, editable, onChange, textarea }) => (
  <div>
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>

    {textarea ? (
      <textarea
        disabled={!editable}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded px-3 py-2 h-24 ${
          !editable && "bg-gray-100"
        }`}
      />
    ) : (
      <input
        disabled={!editable}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded px-3 py-2 ${
          !editable && "bg-gray-100"
        }`}
      />
    )}
  </div>
);

const SocialField = ({
  platform,
  obj,
  editable,
  onChangeUrl,
  onChangeActive,
}) => (
  <div className="border p-3 rounded-lg bg-gray-50">
    <div className="flex justify-between mb-2">
      <span className="text-sm font-semibold text-gray-700">{platform}</span>
      {editable && (
        <select
          value={obj.isActive ? "true" : "false"}
          onChange={(e) => onChangeActive(e.target.value === "true")}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      )}
      {!editable && (
        <span
          className={`text-xs px-2 py-1 rounded ${
            obj.isActive
              ? "bg-green-100 text-green-700"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {obj.isActive ? "Active" : "Inactive"}
        </span>
      )}
    </div>

    <input
      disabled={!editable}
      value={obj.url}
      onChange={(e) => onChangeUrl(e.target.value)}
      placeholder="Social URL"
      className={`w-full border rounded px-3 py-2 text-sm ${
        !editable && "bg-gray-100"
      }`}
    />
  </div>
);

const AdminContacts = () => {
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/contacts");
      const data = res.data || {};
      const initial = {
        directorEmail: data.directorEmail || "",
        cooEmail: data.cooEmail || "",
        phone: data.phone || "",
        whatsappNumber: data.whatsappNumber || "",
        headOfficeAddress: data.headOfficeAddress || "",
        registeredOfficeAddress: data.registeredOfficeAddress || "",
        locationEmbedLink: data.locationEmbedLink || "",

        socialLinks: {
          instagram: {
            url: data.socialLinks?.instagram?.url || "",
            isActive: !!data.socialLinks?.instagram?.isActive,
          },
          facebook: {
            url: data.socialLinks?.facebook?.url || "",
            isActive: !!data.socialLinks?.facebook?.isActive,
          },
          youtube: {
            url: data.socialLinks?.youtube?.url || "",
            isActive: !!data.socialLinks?.youtube?.isActive,
          },
          linkedin: {
            url: data.socialLinks?.linkedin?.url || "",
            isActive: !!data.socialLinks?.linkedin?.isActive,
          },
        },
      };

      setContact(initial);
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load contact details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
  }, []);

  const updateField = (path, value) => {
    setForm((prev) => {
      const updated = structuredClone(prev);
      const keys = path.split(".");
      let ref = updated;

      keys.slice(0, -1).forEach((k) => (ref = ref[k]));
      ref[keys[keys.length - 1]] = value;

      return updated;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const resp = await axiosInstance.put("/contacts", form);
      const updated = resp.data;

      setContact(updated);
      setForm(updated);
      setEditing(false);
      setDirty(false);

      toast.success("Contact details updated");
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(contact);
    setEditing(false);
    setDirty(false);
  };

  if (loading) {
    return <AdminContactsSkeleton />;
  }

  if (!contact) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600 py-10">
          Failed to load contact info
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 ">
      <div className="bg-white border p-6">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Contact Information
          </h2>

          {dirty && editing && (
            <span className="p-2 bg-yellow-100 text-yellow-800 rounded border border-yellow-300 text-sm">
              You have unsaved changes
            </span>
          )}

          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-primaryColor text-white rounded flex items-center gap-2"
            >
              <FaEdit /> Edit
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 rounded flex items-center gap-2"
              >
                <FaTimes /> Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primaryColor text-white rounded flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" /> Saving
                  </>
                ) : (
                  <>
                    <FaCheck /> Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* MAIN FORM GRID */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* CONTACT INFO */}
          <div className="space-y-4">
            {/* EMAIL */}
            <div>
              <InputField
                label="Director Email"
                value={form.directorEmail}
                editable={editing}
                onChange={(v) => updateField("directorEmail", v)}
              />
              {editing && (
                <p className="text-xs text-blue-700 mt-1">
                  Note: Director mail will be shown in the Contact and Get
                  Involved Page
                </p>
              )}
            </div>
            <div>
              <InputField
                label="COO Email"
                value={form.cooEmail}
                editable={editing}
                onChange={(v) => updateField("cooEmail", v)}
              />
              {editing && (
                <p className="text-xs text-blue-700  mt-1">
                  Note: Coo mail will only be shown in the Navbar and Footer
                </p>
              )}
            </div>
            {/* PHONE */}
            <div>
              <InputField
                label="Phone"
                value={form.phone}
                editable={editing}
                onChange={(v) => updateField("phone", v)}
              />

              {editing && (
                <p className="text-xs text-blue-700 mt-1">
                  Tip: Add <strong>+91</strong> and a space before the number.
                  Example: +91 9876543210
                </p>
              )}
            </div>

            {/* WHATSAPP */}
            <div>
              <InputField
                label="WhatsApp Number"
                value={form.whatsappNumber}
                editable={editing}
                onChange={(v) => updateField("whatsappNumber", v)}
              />

              {editing && (
                <p className="text-xs text-blue-700 mt-1">
                  Follow the same format: <strong>+91</strong> and space before
                  the number.
                </p>
              )}
            </div>

            {/* HEAD OFFICE ADDRESS */}
            <div>
              <InputField
                label="Head Office Address"
                value={form.headOfficeAddress}
                editable={editing}
                textarea
                onChange={(v) => updateField("headOfficeAddress", v)}
              />
            </div>

            {/* REGISTERED OFFICE */}
            <InputField
              label="Registered Office Address"
              value={form.registeredOfficeAddress}
              editable={editing}
              textarea
              onChange={(v) => updateField("registeredOfficeAddress", v)}
            />
          </div>

          {/* MAP + SOCIAL LINKS */}
          <div className="space-y-4">
            {/* MAP EMBED */}
            <div>
              <InputField
                label="Google Map Embed Link"
                value={form.locationEmbedLink}
                editable={editing}
                textarea
                onChange={(v) => updateField("locationEmbedLink", v)}
              />

              {editing && (
                <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                  Steps to get the embed link:
                  <br />
                  Google Maps → <strong>Search Location</strong> →
                  <strong> Share</strong> → <strong>Embed Map</strong> → Copy
                  the <strong>src=""</strong> URL from the HTML snippet.
                </p>
              )}
            </div>

            {/* SOCIAL LINKS */}
            <SocialField
              platform="Instagram"
              obj={form.socialLinks.instagram}
              editable={editing}
              onChangeUrl={(v) => updateField("socialLinks.instagram.url", v)}
              onChangeActive={(v) =>
                updateField("socialLinks.instagram.isActive", v)
              }
            />

            <SocialField
              platform="Facebook"
              obj={form.socialLinks.facebook}
              editable={editing}
              onChangeUrl={(v) => updateField("socialLinks.facebook.url", v)}
              onChangeActive={(v) =>
                updateField("socialLinks.facebook.isActive", v)
              }
            />

            <SocialField
              platform="YouTube"
              obj={form.socialLinks.youtube}
              editable={editing}
              onChangeUrl={(v) => updateField("socialLinks.youtube.url", v)}
              onChangeActive={(v) =>
                updateField("socialLinks.youtube.isActive", v)
              }
            />

            <SocialField
              platform="LinkedIn"
              obj={form.socialLinks.linkedin}
              editable={editing}
              onChangeUrl={(v) => updateField("socialLinks.linkedin.url", v)}
              onChangeActive={(v) =>
                updateField("socialLinks.linkedin.isActive", v)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
